/**
 * Communication Status - represents the status of a communication (e.g., Draft, In Progress, Recall, etc.)
 */
export interface CommunicationStatusDto {
  id: number;
  name: string;
  isRecall: boolean;
}

export interface CommunicationStatus {
  id: number;
  name: string;
  isRecall: boolean;
}

/**
 * Communication Contact - simplified representation of a contact or employee in a communication
 */
export interface CommunicationContactDto {
  id: number;
  firstName: string;
  lastName: string | null;
}

export interface CommunicationContact {
  id: number;
  firstName: string;
  lastName: string | null;
}

/**
 * Communication DTO - as returned from the API
 */
export interface CommunicationDto {
  id: number;
  client: CommunicationContactDto;
  employee: CommunicationContactDto;
  status: CommunicationStatusDto;
  notes: string | null;
  communicationDate: string; // ISO date string
  recallAt?: string | null; // ISO datetime string for recall
}

/**
 * Communication domain model
 */
export interface Communication {
  id: number;
  client: CommunicationContact;
  employee: CommunicationContact;
  status: CommunicationStatus;
  notes: string | null;
  communicationDate: Date;
  recallAt?: Date | null;
}

/**
 * Request DTO for creating a new communication
 */
export interface CommunicationCreateRequestDto {
  clientId: number;
  employeeId: number;
  statusId: number;
  notes?: string | null;
  communicationDate?: string | null; // ISO date string, defaults to today if not provided
  recallAt?: string | null; // ISO datetime string, required if status is recall
}

/**
 * Request DTO for updating an existing communication
 */
export interface CommunicationUpdateRequestDto {
  statusId: number;
  notes?: string | null;
  communicationDate: string; // ISO date string
  recallAt?: string | null; // ISO datetime string
}

/**
 * Search/filter parameters for listing communications
 */
export interface CommunicationSearchParams {
  page?: number;
  pageSize?: number;
  clientId?: number;
  employeeId?: number;
  statusId?: number;
  orderByField?: 'id' | 'status' | 'communicationDate';
  orderByDirection?: 'ASC' | 'DESC';
}

// Mapper functions
export const communicationDtoToDomain = (dto: CommunicationDto): Communication => ({
  id: dto.id,
  client: dto.client,
  employee: dto.employee,
  status: dto.status,
  notes: dto.notes,
  communicationDate: new Date(dto.communicationDate),
  recallAt: dto.recallAt ? new Date(dto.recallAt) : null,
});

export const formatCommunicationContactName = (contact: CommunicationContact | null): string => {
  if (!contact) return '';
  return `${contact.firstName} ${contact.lastName || ''}`.trim();
};

export const mapCommunicationSearchParams = (params: CommunicationSearchParams) => ({
  page: params.page ?? 0,
  pageSize: params.pageSize ?? 25,
  clientId: params.clientId,
  employeeId: params.employeeId,
  statusId: params.statusId,
  orderByField: params.orderByField,
  orderByDirection: params.orderByDirection,
});
